import type { CardCorners } from 'blocks/Cards'

import { styled } from '@linaria/react'
import { ButtonSC } from 'atoms/Button'
import { IconSC } from 'atoms/Icon'
import { Checkmark, Label } from 'atoms/RadioButton/RadioButton.styled'
import { TextBoxSC } from 'atoms/TextBox'
import { ButtonGroupWrapper } from 'blocks/ButtonGroup'
import { Wrapper as TextBlockWrapper } from 'blocks/TextBlock'
import { COLOR, FIXED_INDENTS, FONTS, MEDIA } from 'vars/index'

export const PaymentCardHeader = styled.div``
export const PaymentCardBody = styled.div``
export const PaymentOptionCards = styled.div<{
  hasOnePaymentOption: boolean
  columnGap?: FIXED_INDENTS
}>`
  display: flex;
  column-gap: ${({ columnGap }) => `var(${columnGap})`};
  justify-content: ${({ hasOnePaymentOption }) =>
    hasOnePaymentOption ? 'center' : 'space-between'};
  width: 100%;

  ${MEDIA.MAX_767} {
    flex-wrap: wrap;
    row-gap: var(${FIXED_INDENTS.INDENT_40});
    justify-content: center;
  }
`
export const PCWrapper = styled.div``
export const ButtonPayment = styled.div``
export const InputBox = styled.input``
export const InputCardBox = styled.input``
export const LabelInput = styled.label``
export const CircleWrapperIcon = styled.div<{ errorPayment?: boolean }>`
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ errorPayment }) =>
    `var(${errorPayment ? COLOR.ADDITIONAL_RED_2 : COLOR.ADDITIONAL_GREEN})`};

  ${IconSC} {
    position: absolute;
    top: 8px;
    left: 8px;
  }
`

export const InputBlockCardSC = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  width: 100%;
  row-gap: var(${FIXED_INDENTS.INDENT_40});
`

export const InputCreditCardSC = styled.div<{ errorMessage: string }>`
  display: flex;
  align-items: center;
  column-gap: var(${FIXED_INDENTS.INDENT_20});
  width: 100%;

  & > ${IconSC} {
    position: absolute;
    top: -8px;
    right: 0;
    height: 44px;
    width: 44px;
    box-shadow: ${({ errorMessage }) =>
      `inset 0 -1px 1px ${errorMessage ? 0 : '-1px'} var(${
        COLOR.TRANSPARENT
      }) `};
  }

  ${InputBox},
  ${LabelInput} {
    background: var(${COLOR.SURFACE_OPACITY_LIGHT_10});
    color: var(${COLOR.TEXT_ICON_PRIMARY});
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 76px;
    height: 40px;
    border: none;
    font: var(${FONTS.Body4});

    ::placeholder {
      color: var(${COLOR.TEXT_ICON_SECONDARY});
    }

    :focus::placeholder {
      color: transparent;
    }
  }

  ${InputCardBox} {
    background: var(${COLOR.SURFACE_OPACITY_LIGHT_10});
    color: var(${COLOR.TEXT_ICON_PRIMARY});
    text-align: start;
    padding: var(${FIXED_INDENTS.INDENT_20}) var(${FIXED_INDENTS.INDENT_40});
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 40px;
    border: none;
    font: var(${FONTS.Body4});

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }

  & > * {
    box-shadow: ${({ errorMessage }) =>
      `inset 0 -1px 1px 0 var(${
        errorMessage ? COLOR.ADDITIONAL_RED_2 : COLOR.TRANSPARENT
      }) `};
  }

  ${InputBox}[type=number]::-webkit-outer-spin-button,
  ${InputBox}[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${LabelInput} {
    box-shadow: ${({ errorMessage }) =>
      `inset 0 -1px 1px ${errorMessage ? 0 : '-1px'} var(${
        COLOR.TRANSPARENT
      }) `};

    ${InputBox} {
      width: 40px;
      height: 27px;
      border-radius: 4px;
      background: var(${COLOR.SURFACE_OPACITY_LIGHT_10});
      cursor: default;
    }
  }

  ${TextBoxSC} {
    padding-left: 7px;
    box-shadow: ${({ errorMessage }) =>
      `inset 0 -1px 1px ${errorMessage ? 0 : '-1px'} var(${
        COLOR.TRANSPARENT
      }) `};
  }
`

export const CvvDateWrapper = styled.div`
  display: flex;
  width: 100%;
  column-count: 2;
  column-gap: var(${FIXED_INDENTS.INDENT_80});
`

export const CheckboxWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  ${TextBoxSC} {
    padding-left: 26px;
  }
`

export const PaymentOptionPopupSC = styled.div<{
  rowGap?: FIXED_INDENTS
}>`
  --widthBlockNewCard: 464px;

  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  row-gap: ${({ rowGap }) => `var(${rowGap})`};

  ${ButtonSC} {
    width: auto;
    min-width: 132px;
  }

  ${TextBlockWrapper} {
    align-items: center;
  }

  ${ButtonPayment} {
    padding-top: var(${FIXED_INDENTS.INDENT_30});
  }

  & > ${TextBoxSC} {
    margin-bottom: var(${FIXED_INDENTS.INDENT_20});
    height: 100%;
  }

  ${ButtonGroupWrapper} {
    ${ButtonPayment} {
      padding-top: 0;

      ${ButtonSC} {
        width: 171px;
      }
    }
  }

  ${InputBlockCardSC},
  ${CvvDateWrapper},
  ${CheckboxWrapper} {
    max-width: var(--widthBlockNewCard);
  }

  ${InputBlockCardSC} {
    ${TextBlockWrapper} {
      align-items: flex-start;
    }
  }
`

export const PaymentMentionPopupSC = styled(PaymentOptionPopupSC)`
  ${MEDIA.MAX_599} {
    margin: auto;
  }
`

export const PaymentCardSC = styled.div<{
  paddings: FIXED_INDENTS[]
  corners?: CardCorners
}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 152px;
  padding: ${({ paddings }) =>
    paddings && paddings.length > 0
      ? paddings.map((item) => `var(${item})`)?.join(' ')
      : 0};
  border-radius: ${({ corners }) => (corners ? `var(${corners})` : 0)};

  ${PaymentCardHeader} {
    display: flex;
    justify-content: space-between;
  }

  ${PaymentCardBody} {
    display: flex;
    flex-direction: column;
  }
`

export const PaymentCardWrapper = styled.div<{
  checked?: boolean
  backgroundColor?: COLOR
  backgroundColorHovered?: COLOR
  backgroundColorPressed?: COLOR
  backgroundColorSelected?: COLOR
  backgroundColorSelectedHovered?: COLOR
  backgroundColorSelectedPressed?: COLOR
  isActiveCard?: boolean
  paddings?: FIXED_INDENTS[]
  icon?: boolean
  isCard?: boolean
}>`
  position: relative;
  flex: 0 1 49%;
  cursor: pointer;

  width: ${({ isActiveCard }) => (isActiveCard ? '100%' : 'auto')};
  max-width: ${({ isActiveCard }) => (isActiveCard ? '428px' : '296px')};
  min-width: ${({ isActiveCard }) => (isActiveCard ? 'unset' : '296px')};

  ${PaymentCardSC} {
    background: ${({ checked, backgroundColor, backgroundColorSelected }) =>
      checked ? `var(${backgroundColorSelected})` : `var(${backgroundColor})`};

    ${MEDIA.NO_TOUCH} {
      &:hover {
        background: ${({
          checked,
          backgroundColorHovered,
          backgroundColorSelectedHovered,
        }) =>
          checked
            ? `var(${backgroundColorSelectedHovered})`
            : `var(${backgroundColorHovered})`};
      }
    }

    &:active {
      background: ${({
        checked,
        backgroundColorPressed,
        backgroundColorSelectedPressed,
      }) =>
        checked
          ? `var(${backgroundColorSelectedPressed})`
          : `var(${backgroundColorPressed})`};
    }
  }

  ${Label} {
    position: absolute;
    top: ${({ isCard }) =>
      isCard ? '50%' : `var(${FIXED_INDENTS.INDENT_60})`};
    transform: translateY(
      ${({ isCard }) => (isCard ? '-50%' : `var(${FIXED_INDENTS.INDENT_0})`)}
    );
    right: 33px;

    &:hover input ~ ${Checkmark}, &.hover input ~ ${Checkmark} {
      background: var(${COLOR.SURFACE_OPACITY_DARK_40});
      border-color: var(${COLOR.SURFACE_OPACITY_LIGHT_10});
    }

    ${MEDIA.NO_TOUCH} {
      &:hover + ${PaymentCardSC} {
        background: ${({
          checked,
          backgroundColorHovered,
          backgroundColorSelectedHovered,
        }) =>
          checked
            ? `var(${backgroundColorSelectedHovered})`
            : `var(${backgroundColorHovered})`};
      }
    }

    ${MEDIA.NO_TOUCH} {
      &:hover + ${PCWrapper} {
        ${PaymentCardSC} {
          background: ${({
            checked,
            backgroundColorHovered,
            backgroundColorSelectedHovered,
          }) =>
            checked
              ? `var(${backgroundColorSelectedHovered})`
              : `var(${backgroundColorHovered})`};
        }
      }
    }
    &:active + ${PaymentCardSC} {
      background: ${({
        checked,
        backgroundColorPressed,
        backgroundColorSelectedPressed,
      }) =>
        checked
          ? `var(${backgroundColorSelectedPressed})`
          : `var(${backgroundColorPressed})`};
    }

    &:active + ${PCWrapper} {
      ${PaymentCardSC} {
        background: ${({
          checked,
          backgroundColorPressed,
          backgroundColorSelectedPressed,
        }) =>
          checked
            ? `var(${backgroundColorSelectedPressed})`
            : `var(${backgroundColorPressed})`};
      }
    }

    &:hover input:checked ~ ${Checkmark}, &.hover input:checked ~ ${Checkmark} {
      border: none;
    }

    ${Checkmark} {
      border-radius: 4px;

      &:after {
        display: none !important;
      }

      ${IconSC} {
        display: ${({ checked }) => (checked ? 'block' : 'none')};
        top: 1px;
        left: 1px;
        width: 1.1em;
        height: 1.1em;
      }
    }
  }

  ${PCWrapper} {
    ${PaymentCardSC} {
      height: 48px;
      min-height: 48px;
      flex-direction: row;
      align-items: center;
      justify-content: ${({ isCard }) => (isCard ? 'space-between' : 'center')};
      padding: ${({ paddings, isCard }) =>
        isCard && paddings && paddings.length > 0
          ? paddings.map((item) => `var(${item})`)?.join(' ')
          : 0};

      & > ${IconSC} {
        margin-left: var(${FIXED_INDENTS.INDENT_40});
        height: 40px;
        width: 40px;
      }
    }
  }
`

export const ErrorMessageWrapper = styled.div`
  margin-top: -8px;
`
